<script setup lang="ts">
import {
  ElMessage,
} from "element-plus";
import AddonCard from "~/components/AddonCard.vue";
import PaymentTotalAmount from "~/components/payment-checkout/PaymentTotalAmount.vue";
import {
  getApiErrorMessage,
} from "~/composables/useApiFetch";

type PurchaseScope = "personal" | "organization";

type CreditPackage = {
  key: string;
  name: string;
  description?: string;
  price: number;
  credits: number;
};

type CouponPrice = {
  templateKey: string;
  couponCode: string;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
};

type CouponPreview = {
  couponCode: string;
  discountType: "percentage" | "fixed_amount";
  discountValue: number;
  addons: CouponPrice[];
};

const props = withDefaults(defineProps<{
  scope?: PurchaseScope;
  modelValue: CreditPackage | null;
  packages?: CreditPackage[];
  tenant?: string;
  disabled?: boolean;
}>(), {
  scope: "personal",
  packages: () => [],
  tenant: "",
  disabled: false,
});

const emit = defineEmits<{
  (event: "update:modelValue", value: CreditPackage): void;
  (event: "update:couponCode", value: string): void;
  (event: "update:couponDirty", value: boolean): void;
}>();

const {
  $t,
} = useNuxtApp();
const apiFetch = useApiFetch();
const personalPackages = ref<CreditPackage[]>([]);
const loadingPackages = ref(false);
const applyingCoupon = ref(false);
const couponCode = ref("");
const appliedCouponCode = ref("");
const couponPrices = ref<Record<string, CouponPrice>>({});
const paymentSection = ref<HTMLElement | null>(null);
const creditCostsReady = ref(false);
const {
  costs: trainingCreditCosts,
  load: loadTrainingCreditCosts,
} = useTrainingCreditCosts();

const isOrganization = computed(() => props.scope === "organization");
const availablePackages = computed(() => isOrganization.value ? props.packages : personalPackages.value);
const labels = computed(() => isOrganization.value
  ? {
      subtitle: $t("TENANT_ADMIN.USAGE.ADDON_EXPLANATION"),
      validityTitle: $t("TENANT_ADMIN.USAGE.CREDIT_VALIDITY_TITLE"),
      validityNotice: $t("TENANT_ADMIN.USAGE.CREDIT_VALIDITY_NOTICE"),
      couponCode: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_CODE"),
      couponPlaceholder: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_PLACEHOLDER"),
      applyCoupon: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_APPLY"),
      removeCoupon: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_REMOVE"),
      couponApplied: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_APPLIED"),
      couponAppliedWithCode: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_APPLIED_WITH_CODE", {
        code: appliedCouponCode.value,
      }),
      couponInvalid: $t("TENANT_ADMIN.USAGE.ADDON_COUPON_INVALID"),
      packageSelected: $t("TENANT_ADMIN.USAGE.ADDON_PACKAGE_SELECTED"),
      selectPackage: $t("TENANT_ADMIN.USAGE.SELECT_ADDON"),
      cardCredits: $t("TENANT_ADMIN.USAGE.ADDON_CARD_CREDITS"),
      cardPrice: $t("TENANT_ADMIN.USAGE.ADDON_CARD_PRICE"),
      cardBalance: $t("TENANT_ADMIN.USAGE.ADDON_CARD_BALANCE"),
      cardUpTo: $t("TENANT_ADMIN.USAGE.ADDON_CARD_UP_TO"),
      cardInterviewTrainings: $t("TENANT_ADMIN.USAGE.ADDON_CARD_INTERVIEW_TRAININGS"),
      cardDialogTrainings: $t("TENANT_ADMIN.USAGE.ADDON_CARD_DIALOG_TRAININGS"),
      cardCapacityUnavailable: $t("TENANT_ADMIN.USAGE.ADDON_CARD_CAPACITY_UNAVAILABLE"),
      finalAmount: $t("TENANT_ADMIN.USAGE.ADDON_FINAL_AMOUNT"),
      qrPaymentTitle: $t("TENANT_ADMIN.USAGE.ADDON_QR_PAYMENT_TITLE"),
      paymentNotice: $t("TENANT_ADMIN.USAGE.ADDON_PAYMENT_QR_NOTICE"),
    }
  : {
      subtitle: $t("MY_CREDITS_USAGE.BUY_CREDITS_DIALOG_SUBTITLE"),
      validityTitle: $t("MY_CREDITS_USAGE.CREDIT_VALIDITY_TITLE"),
      validityNotice: $t("MY_CREDITS_USAGE.CREDIT_VALIDITY_NOTICE"),
      couponCode: $t("MY_CREDITS_USAGE.COUPON_CODE"),
      couponPlaceholder: $t("MY_CREDITS_USAGE.COUPON_PLACEHOLDER"),
      applyCoupon: $t("MY_CREDITS_USAGE.APPLY_COUPON"),
      removeCoupon: $t("MY_CREDITS_USAGE.REMOVE_COUPON"),
      couponApplied: $t("MY_CREDITS_USAGE.COUPON_APPLIED"),
      couponAppliedWithCode: $t("MY_CREDITS_USAGE.COUPON_APPLIED_WITH_CODE", {
        code: appliedCouponCode.value,
      }),
      couponInvalid: $t("MY_CREDITS_USAGE.COUPON_INVALID"),
      packageSelected: $t("MY_CREDITS_USAGE.PACKAGE_SELECTED"),
      selectPackage: $t("MY_CREDITS_USAGE.SELECT_PACKAGE"),
      cardCredits: $t("MY_CREDITS_USAGE.PERSONAL_CREDITS"),
      cardPrice: $t("MY_CREDITS_USAGE.PACKAGE_CARD_PRICE"),
      cardBalance: $t("MY_CREDITS_USAGE.PACKAGE_CARD_BALANCE"),
      cardUpTo: $t("MY_CREDITS_USAGE.PACKAGE_CARD_UP_TO"),
      cardInterviewTrainings: $t("MY_CREDITS_USAGE.PACKAGE_CARD_INTERVIEW_TRAININGS"),
      cardDialogTrainings: $t("MY_CREDITS_USAGE.PACKAGE_CARD_DIALOG_TRAININGS"),
      cardCapacityUnavailable: $t("MY_CREDITS_USAGE.PACKAGE_CARD_CAPACITY_UNAVAILABLE"),
      finalAmount: $t("MY_CREDITS_USAGE.FINAL_AMOUNT"),
      qrPaymentTitle: $t("MY_CREDITS_USAGE.QR_PAYMENT_TITLE"),
      paymentNotice: $t("MY_CREDITS_USAGE.PAYMENT_QR_NOTICE"),
    });

const couponNeedsApply = computed(() => {
  return couponCode.value.trim().toUpperCase() !== appliedCouponCode.value;
});

const selectedCouponPrice = computed(() => {
  return props.modelValue ? couponPrices.value[props.modelValue.key] ?? null : null;
});

watch(couponNeedsApply, (value) => {
  emit("update:couponDirty", value);
}, {immediate: true});

const syncSelectedCoupon = (packageKey?: string) => {
  emit("update:couponCode", packageKey && couponPrices.value[packageKey] ? appliedCouponCode.value : "");
};

const selectPackage = async (pkg: CreditPackage) => {
  if (props.disabled) {
    return;
  }
  emit("update:modelValue", pkg);
  syncSelectedCoupon(pkg.key);
  await nextTick();
  paymentSection.value?.scrollIntoView({behavior: "smooth", block: "start"});
};

const applyCoupon = async () => {
  const code = couponCode.value.trim();
  if (!code || (isOrganization.value && !props.tenant)) {
    return;
  }
  applyingCoupon.value = true;
  try {
    const preview = await apiFetch<CouponPreview>(
      isOrganization.value
        ? "/payments/tenant-addon-orders/coupon-preview"
        : "/plans/coupons/preview/single-user-addons",
      {
        method: "POST",
        body: isOrganization.value
          ? {tenant: props.tenant, couponCode: code}
          : {couponCode: code},
      },
    );
    couponPrices.value = Object.fromEntries(preview.addons.map((addon) => [addon.templateKey, addon]));
    appliedCouponCode.value = preview.couponCode;
    couponCode.value = preview.couponCode;
    syncSelectedCoupon(props.modelValue?.key);
    ElMessage.success(labels.value.couponApplied);
  } catch (error) {
    ElMessage.error(getApiErrorMessage(error, labels.value.couponInvalid));
  } finally {
    applyingCoupon.value = false;
  }
};

const removeCoupon = () => {
  couponCode.value = "";
  appliedCouponCode.value = "";
  couponPrices.value = {};
  emit("update:couponCode", "");
};

const calculateTrainingCapacity = (credits: number, cost: number | undefined) => {
  if (!Number.isFinite(credits) || !cost || cost <= 0) {
    return null;
  }
  return Math.floor(credits / cost);
};

const fetchPersonalPackages = async () => {
  if (isOrganization.value) {
    return;
  }
  loadingPackages.value = true;
  try {
    personalPackages.value = await apiFetch<CreditPackage[]>("/api/auth/public-plans");
  } catch {
    ElMessage.error($t("MY_CREDITS_USAGE.PACKAGES_LOAD_FAILED"));
  } finally {
    loadingPackages.value = false;
  }
};

onMounted(async () => {
  await Promise.all([
    fetchPersonalPackages(),
    loadTrainingCreditCosts(),
  ]);
  creditCostsReady.value = true;
});
</script>

<template>
  <div class="space-y-6">
    <p class="text-sm text-gray-500 dark:text-gray-400">
      {{ labels.subtitle }}
    </p>

    <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
      <div class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
        {{ labels.couponCode }}
      </div>
      <div class="flex flex-col gap-2 sm:flex-row">
        <el-input
          v-model="couponCode"
          :placeholder="labels.couponPlaceholder"
          maxlength="40"
          clearable
          :disabled="disabled || applyingCoupon"
          @keyup.enter="applyCoupon"
          @clear="removeCoupon"
        />
        <el-button
          type="primary"
          plain
          :loading="applyingCoupon"
          :disabled="disabled || !couponCode.trim() || !couponNeedsApply"
          @click="applyCoupon"
        >
          {{ labels.applyCoupon }}
        </el-button>
        <el-button
          v-if="appliedCouponCode"
          :disabled="disabled || applyingCoupon"
          @click="removeCoupon"
        >
          {{ labels.removeCoupon }}
        </el-button>
      </div>
      <div
        v-if="appliedCouponCode"
        class="mt-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300"
      >
        {{ labels.couponAppliedWithCode }}
      </div>
    </div>

    <div
      v-if="loadingPackages || !creditCostsReady"
      class="flex justify-center py-8"
    >
      <base-loader
        :size="24"
        class="text-primary-500"
      />
    </div>

    <div
      v-else
      class="grid grid-cols-1 items-stretch gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3"
    >
      <AddonCard
        v-for="pkg in availablePackages"
        :key="pkg.key"
        :title="pkg.name"
        :credits="pkg.credits"
        :credits-label="labels.cardCredits"
        :price-label="labels.cardPrice"
        :balance-text="labels.cardBalance"
        :up-to-label="labels.cardUpTo"
        :interview-trainings-label="labels.cardInterviewTrainings"
        :dialog-trainings-label="labels.cardDialogTrainings"
        :capacity-unavailable-text="labels.cardCapacityUnavailable"
        :interview-capacity="calculateTrainingCapacity(pkg.credits, trainingCreditCosts?.interview)"
        :dialog-capacity="calculateTrainingCapacity(pkg.credits, trainingCreditCosts?.dialog)"
        :price="couponPrices[pkg.key]?.finalAmount ?? pkg.price"
        :original-price="couponPrices[pkg.key] ? pkg.price : null"
        :is-selected="modelValue?.key === pkg.key"
        :button-text="modelValue?.key === pkg.key ? labels.packageSelected : labels.selectPackage"
        :button-type="modelValue?.key === pkg.key ? 'success' : 'primary'"
        :button-disabled="disabled"
        whole-card-clickable
        compact-on-mobile
        @select="selectPackage(pkg)"
      />
    </div>

    <PaymentTotalAmount
      v-if="modelValue"
      ref="paymentSection"
      class="scroll-mt-4"
      :label="labels.finalAmount"
      :amount="selectedCouponPrice?.finalAmount ?? modelValue.price"
      :original-amount="selectedCouponPrice?.originalAmount"
    />
  </div>
</template>
